import { sum } from 'lib/array/arrayUtils'
import { LOYALTY_PAYMENT_METHOD_BONUS_IBS_EARN_KEY } from 'loyalty/constants/loyaltyPaymentMethodEarn'

/**
 * True for the svc-lux-loyalty rule that adds bonus points for paying with a low cost payment method.
 * That bonus has its own messaging, so it must not be counted as a bonus points campaign.
 */
export function isLoyaltyPaymentMethodEarnRule(rule: App.LoyaltyCustomRuleBreakdown): boolean {
  return rule.ibsEarnKey === LOYALTY_PAYMENT_METHOD_BONUS_IBS_EARN_KEY
}

function isNotLoyaltyPaymentMethodEarnRule(rule: App.LoyaltyCustomRuleBreakdown): boolean {
  return !isLoyaltyPaymentMethodEarnRule(rule)
}

export function excludeLoyaltyPaymentMethodEarnRules(
  rules: ReadonlyArray<App.LoyaltyCustomRuleBreakdown>,
): Array<App.LoyaltyCustomRuleBreakdown> {
  return rules.filter(isNotLoyaltyPaymentMethodEarnRule)
}

/**
 * Counts the promotions that add bonus points: distinct custom rules other than the payment-method bonus.
 */
export function countLoyaltyBonusPointsPromotions(rules: ReadonlyArray<App.LoyaltyCustomRuleBreakdown>): number {
  const promotionRuleIds = excludeLoyaltyPaymentMethodEarnRules(rules)
    .filter(rule => rule.pointsType === 'Bonus Points' && rule.points > 0)
    .map(rule => rule.id)

  return new Set(promotionRuleIds).size
}

export function getLoyaltyPaymentMethodBonusPoints(
  rules: ReadonlyArray<App.LoyaltyCustomRuleBreakdown>,
): number {
  return sum(rules.filter(isLoyaltyPaymentMethodEarnRule), rule => rule.pointsType === 'Bonus Points' ? rule.points : 0)
}

interface EarnablePaymentMethodBonusParams {
  customRulesBreakdown: ReadonlyArray<App.LoyaltyCustomRuleBreakdown>
  initialBonusPoints: number
  earnableBonusPoints: number
}

/**
 * The payment-method bonus's share of an item's earnable bonus points. Checkout scales the earnable
 * bonus down for promo codes, points burn and credit, so the payment-method bonus is scaled by the same amount.
 */
export function getEarnableLoyaltyPaymentMethodBonusPoints({
  customRulesBreakdown,
  initialBonusPoints,
  earnableBonusPoints,
}: EarnablePaymentMethodBonusParams): number {
  if (initialBonusPoints <= 0) return 0

  const initialPaymentMethodBonusPoints = getLoyaltyPaymentMethodBonusPoints(customRulesBreakdown)
  return Math.round(earnableBonusPoints * (initialPaymentMethodBonusPoints / initialBonusPoints))
}
